import { LeafIcon } from '@/components/icons'
import LayersIcon from '@/components/icons/LayersIcon'
import PackVariantsIcon from '@/components/icons/PackVariantsIcon'
import WaterLevelSoilDarkIcon from '@/components/icons/WaterLevelSoilDarkIcon'
import useTranslation from '@/hooks/useTranslation'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import { Box, Stack, Typography } from '@mui/material'
import {
  ComponentType,
  FC,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import AreasAndZones from '../AreasAndZones/AreasAndZones'
import Emitter from '../Emitter'
import Plants from '../Plants'
import Soil from '../Soil'

interface SectionConfig {
  icon: FC<{ sx: { marginRight: string; color: string } }>
  title: string
  component: ComponentType<{ titleSlot?: ReactNode }>
  // Relative width. The Plant/Soil/Emitter lists are single-column and stay
  // narrow; Areas & Sub-Areas has a tree + action column, so it takes a bit
  // more (but is capped so it doesn't dominate on wide screens).
  grow: number
  minWidth: number
  maxWidth?: number
  /** Takes the whole second row when the layout wraps. */
  wrapFullWidth?: boolean
}

/**
 * Below this the four columns cannot all hold a usable width, so the row wraps
 * to two. 1092px is what the minimums add up to (200 x 3 + 420 + three 24px
 * gaps); the extra allows for the page's own padding.
 *
 * Measured on the row itself rather than the viewport: the sidebar expands and
 * collapses, which changes the space these panels have without the window
 * changing size at all.
 */
const WRAP_BREAKPOINT = 1140
/** Each panel keeps a readable list height once they sit on two rows. */
const WRAPPED_PANEL_HEIGHT = 420

// Plant, Soil and Emitter share the same crop/media configuration context, so
// they live under a single Data Configuration tab and are shown side by side in
// three columns (rather than as three separate top-level tabs). Each column
// keeps its own toolbar and bulk-edit state.
const PlantSoilEmitter = () => {
  const { t } = useTranslation()
  const themeMode = useSelector(selectTheme)
  const isDark = themeMode === mode_type.dark
  const accent = isDark ? '#fff' : '#004744'

  const sections: SectionConfig[] = [
    {
      icon: LeafIcon,
      title: t('menuDataConfigPlant'),
      component: Plants,
      grow: 1,
      minWidth: 200,
    },
    {
      icon: WaterLevelSoilDarkIcon,
      title: t('menuDataConfigSoil'),
      component: Soil,
      grow: 1,
      minWidth: 200,
    },
    {
      icon: PackVariantsIcon,
      title: t('menuDataConfigEmitter'),
      component: Emitter,
      // Carries an Efficiency column the other two do not, so it takes a
      // larger share rather than scrolling it out of sight.
      grow: 1.4,
      minWidth: 260,
    },
    {
      icon: LayersIcon,
      title: t('navigationConfigAreasAndSubAreas'),
      component: AreasAndZones,
      // Narrower than it was: its action column no longer flexes, so the tree
      // needs less around it, and the space goes to Emitter.
      grow: 1.1,
      // Bulk edit adds a select column, so the floor covers checkbox + tree +
      // action (56 + 200 + 52) plus the panel's own padding.
      minWidth: 340,
      maxWidth: 460,
      // Full width on its own row once the layout wraps.
      wrapFullWidth: true,
    },
  ]

  const rowRef = useRef<HTMLDivElement>(null)
  const [wrapped, setWrapped] = useState(false)

  useLayoutEffect(() => {
    const el = rowRef.current
    if (!el) return
    const measure = () => setWrapped(el.clientWidth < WRAP_BREAKPOINT)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const renderSection = ({
    icon: Icon,
    title,
    component: ActiveComponent,
    grow,
    minWidth,
    maxWidth,
    wrapFullWidth,
  }: SectionConfig): ReactNode => (
    <Box
      key={title}
      sx={{
        // Columns size by their relative grow weight, with a floor (and an
        // optional cap so Areas doesn't balloon on wide screens); if they don't
        // fit the row scrolls horizontally instead of squashing them.
        flex: `${grow} 1 0`,
        minWidth: `${minWidth}px`,
        ...(maxWidth && !wrapped ? { maxWidth: `${maxWidth}px` } : {}),
        ...(wrapped
          ? wrapFullWidth
            ? { flexBasis: '100%', maxWidth: 'none', height: WRAPPED_PANEL_HEIGHT }
            : { flexBasis: '260px', height: WRAPPED_PANEL_HEIGHT }
          : {}),
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <ActiveComponent
        titleSlot={
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <Icon sx={{ marginRight: '0px', color: accent }} />
            <Typography
              sx={{ color: 'text.main', fontWeight: 700, fontSize: '14px' }}
            >
              {title}
            </Typography>
          </Stack>
        }
      />
    </Box>
  )

  return (
    <Box
      ref={rowRef}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'stretch',
        pb: '12px',
        gap: '24px',
        // Four columns need 1092px of minimums. Below that they used to
        // squeeze to their 200px floor — too narrow to read, and narrow enough
        // that each list scrolled its own columns out of sight. Wrapping puts
        // Plant/Soil/Emitter on one row and Areas on the next, so every panel
        // gets roughly double the width instead.
        ...(wrapped
          ? { height: 'auto', minHeight: '100%', overflowY: 'auto' }
          : { height: '100%' }),
      }}
    >
      {sections.map(renderSection)}
    </Box>
  )
}

export default PlantSoilEmitter
